/* Augments Phase 2's stub with BackButton, MainButton, haptic, share. */

type TgWebApp = {
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  MainButton?: {
    setText: (s: string) => void;
    show: () => void;
    hide: () => void;
    setParams: (p: Record<string, unknown>) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    enable: () => void;
    disable: () => void;
  };
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
  };
  shareMessage?: (msg: string, opts?: { url?: string }) => void;
  openLink?: (url: string, opts?: { try_instant_view?: boolean }) => void;
  openTelegramLink?: (url: string) => void;
  initData?: string;
  initDataUnsafe?: { user?: { photo_url?: string } };
  ready?: () => void;
  expand?: () => void;
};

function tg(): TgWebApp | null {
  return ((window as unknown) as { Telegram?: { WebApp?: TgWebApp } }).Telegram?.WebApp ?? null;
}

let _backCb: (() => void) | null = null;

export const backButton = {
  show(callback: () => void) {
    const w = tg()?.BackButton;
    if (!w) return;
    if (_backCb) w.offClick(_backCb);
    _backCb = callback;
    w.onClick(_backCb);
    w.show();
  },
  hide() {
    const w = tg()?.BackButton;
    if (!w) return;
    if (_backCb) {
      w.offClick(_backCb);
      _backCb = null;
    }
    w.hide();
  },
};

let _mainCb: (() => void) | null = null;

export const mainButton = {
  set(text: string, callback: () => void, opts: { disabled?: boolean } = {}) {
    const w = tg()?.MainButton;
    if (!w) return;
    if (_mainCb) w.offClick(_mainCb);
    _mainCb = callback;
    w.setText(text);
    w.onClick(_mainCb);
    if (opts.disabled) w.disable();
    else w.enable();
  },
  show() {
    tg()?.MainButton?.show();
  },
  hide() {
    const w = tg()?.MainButton;
    if (!w) return;
    if (_mainCb) {
      w.offClick(_mainCb);
      _mainCb = null;
    }
    w.hide();
  },
};

export const haptic = {
  impact(style: 'light' | 'medium' | 'heavy' = 'light') {
    tg()?.HapticFeedback?.impactOccurred(style);
  },
  notification(type: 'success' | 'warning' | 'error') {
    tg()?.HapticFeedback?.notificationOccurred(type);
  },
};

/** Open an external URL in the system browser, not inside the Mini App.
 * Falls back to `window.open` for non-Telegram environments. */
export function openExternalLink(url: string): void {
  const w = tg();
  if (w?.openLink) {
    w.openLink(url, { try_instant_view: false });
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export async function share(url: string, text: string): Promise<'shared' | 'copied'> {
  const w = tg();
  if (w?.shareMessage) {
    try {
      w.shareMessage(text, { url });
      return 'shared';
    } catch {
      // fall through to clipboard
    }
  }
  await navigator.clipboard.writeText(`${text}\n${url}`);
  return 'copied';
}

export function useTelegramWebApp() {
  return { backButton, mainButton, haptic, share };
}
